import { FC, useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './OrganizationCommoditiesPage.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { fetchLandscapes } from '../../../store/landscapeAPI/landscape/reducers/landscape.slice';
import Spinner from '../../atoms/Spinner/Spinner';
import { OrganizationCommoditiesTemplate } from '../../templates';
import {
  deleteCompactSupporter,
  fetchCompactSupportersByOrganizationId,
  saveCompactSupporter,
} from '../../../store/tradeAPI/commodity/reducers/landscape-supporters.slice';
import { useParams } from 'react-router-dom';
import { OrganizationLandscapeSupporterModal } from '../../organisms';
import { LandscapeSupportersSaveRequestDto } from '../../../store/tradeAPI/commodity/dto/request/landscape-suporters-save-request.dto';
import { LandscapeBuyerRelationType } from '../../../store/landscapeAPI/landscape/landscape-buyer-relation-type';
import { getUUID } from '../../../utils/UUID';
import { fetchCommoditiesByLandscapeId } from '../../../store/tradeAPI/commodity/reducers/commodity-landscape.slice';
import { LandscapeCommodityResponseDto } from '../../../store/landscapeAPI/landscape/dto/response/landscape-commodity-response.dto';
import { LandscapeResponseDto } from '../../../store/landscapeAPI/landscape/dto/response/landscape-response.dto';
import { LandscapeSupportersResponseDto } from '../../../store/tradeAPI/commodity/dto/response/landscape-suporters-response.dto';

interface OrganizationCommoditiesPageProps {}

interface TableItem {
  id: string;
  landscapeName: string;
  relationType: LandscapeBuyerRelationType;
}

const OrganizationCommoditiesPage: FC<
  OrganizationCommoditiesPageProps
> = () => {
  const { organizationId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [data, setData] = useState<TableItem[]>();
  const [selectedLandscape, setSelectedLandscape] = useState<string>();
  const [commodities, setCommodities] =
    useState<LandscapeCommodityResponseDto[]>();
  const [supporter, setSupporter] = useState<LandscapeSupportersSaveRequestDto>(
    {
      organizationId: organizationId ?? '',
      compactId: '',
      commodityId: '',
      relationType: LandscapeBuyerRelationType.Funders,
      id: getUUID(),
      published: true,
    }
  );

  const { landscapes, status: landscapeStatus } = useSelector(
    (state: RootState) => state.landscapeSlice
  );
  const { compactSupporters: supporters, status: supporterStatus } =
    useSelector((state: RootState) => state.landscapeSupportersSlice);

  const { status: commodityStatus } = useSelector(
    (state: RootState) => state.commodityLandscapeSlice
  );

  useEffect(() => {
    dispatch(fetchLandscapes());
    dispatch(fetchCompactSupportersByOrganizationId(organizationId!));
  }, [dispatch, organizationId]);

  useEffect(() => {
    const fetchData = async (
      supporters: LandscapeSupportersResponseDto[],
      landscapes: LandscapeResponseDto[]
    ) => {
      const filteredTableItems: TableItem[] = supporters.map((supporter) => {
        const matchingLandscape = landscapes.find(
          (landscape) => landscape.id === supporter.compactId
        );

        return {
          id: supporter.id,
          landscapeName: matchingLandscape?.name ?? '',
          relationType: supporter.relationType,
        };
      });

      setData(filteredTableItems);
    };
    if (supporters && landscapes) {
      fetchData(supporters, landscapes);
    }
  }, [supporters, landscapes, dispatch]);

  useEffect(() => {
    if (!selectedLandscape) return;
    dispatch(fetchCommoditiesByLandscapeId(selectedLandscape))
      .unwrap()
      .then((resp) => setCommodities(resp));
  }, [selectedLandscape, dispatch]);

  const handleOnChange = (supporter: LandscapeSupportersSaveRequestDto) => {
    setSupporter(supporter);
  };

  const handleDelete = async (id: string) => {
    await dispatch(deleteCompactSupporter(id));
    dispatch(fetchCompactSupportersByOrganizationId(organizationId!));
  };

  const callbackFn = async (supporter: LandscapeSupportersSaveRequestDto) => {
    await dispatch(saveCompactSupporter(supporter));
    dispatch(fetchCompactSupportersByOrganizationId(organizationId!));
    setShowModal(false);
  };

  if (
    landscapeStatus === 'loading' ||
    supporterStatus === 'loading' ||
    commodityStatus === 'loading'
  ) {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.wrapper)}>
      {showModal && (
        <OrganizationLandscapeSupporterModal
          show={showModal}
          setShow={setShowModal}
          setSelectedLandscape={setSelectedLandscape}
          selectedLandscape={selectedLandscape}
          landscapes={landscapes ?? []}
          commodities={commodities ?? []}
          landscapeSupporter={supporter}
          handleOnChange={handleOnChange}
          callbackFn={callbackFn}
        />
      )}
      <OrganizationCommoditiesTemplate
        setShow={setShowModal}
        data={data ?? []}
        handleDelete={handleDelete}
      />
    </div>
  );
};

export default OrganizationCommoditiesPage;
